/* istanbul ignore file */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select/select";
import { useCallback, useState } from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { jwtDecode } from "jwt-decode";
import { routerMode } from "../../config.json";

const languages = [
  {
    name: "Danish",
    value: "da-DK",
  },
  {
    name: "English",
    value: "en-US",
  },
  {
    name: "Hungarian",
    value: "hu-HU",
  },
  {
    name: "Japanese",
    value: "ja-JP",
  },
  {
    name: "Chinese (Simplified)",
    value: "zh-CN",
  },
  {
    name: "Chinese (Traditional, Taiwan)",
    value: "zh-TW",
  },
];

const token = {
  header: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9",
  body: "eyJuYW1laWQiOiIxMDczNzQyMTA0IiwidW5pcXVlX25hbWUiOiJlbWVhXFxBOTk4NjM5IiwidXJuOmZ1bGxuYW1lIjoiQTk5ODYzOSIsInVybjpzZXNzaW9uIjoiN0U4MTI3QzhENENCMjNCRjE2NkI5ODU1MEREQTJFN0E4Nzk1NTkyMkVCRjg0ODBCQzkxM0M0MjBBQ0VERDg3RkI2RkU0MDczRDJGNDk4N0U4QTRFRjc3N0ExRTI4RDQ3NjU1QzRFNzMwMjZGNzg3QTJGMEZCMEUxMEVENDY3NEEiLCJ1cm46c2Vzc2lvbmV4cGlyZXMiOiI3MzQiLCJ1cm46bGFuZ3VhZ2UiOiJlbi1VUyIsImlzcyI6InVybjp1bmlmaWVkb2F1dGgiLCJ1cm46cmVhbG0iOiJ1cm46dW1jIiwidXJuOmNvbW1lbnQiOiIiLCJ1cm46Z3JvdXAiOiIyfEVNRUFcXERLLUctT3BDZW50ZXJTeXN0ZW1BZG1pbiIsInVybjphdHRyaWJ1dGUiOlsiVVNFUl9DT01NRU5UfEZyZWRlcmlrIFZlbWJ5ZSAtIEEiLCJVU0VSX0ZJUlNUX05BTUV8RnJlZGVyaWsiLCJVU0VSX0xBU1RfTkFNRXxWZW1ieWUiLCJVU0VSX0VNQUlMXzF8IiwiVVNFUl9QSE9ORXwiLCJVU0VSX01PQklMRXwiLCJVU0VSX0lOSVRJQUxTfCIsIlVTRVJfVVNOQ0hBTkdFRHwzODg4NzYwNjMiLCJDdWx0dXJlVUl8dW5kIiwiQ3VsdHVyZXx1bmQiXSwidXJuOm9yaWdpbmF0b3IiOiJEZWZhdWx0UGxhbnRfT3BlcmF0b3JQYWdlIiwidXJuOmlkcF9hZGRyZXNzIjoiaHR0cHM6Ly9hemVvcGV4ZHNzMDQuZW1lYS5ncm91cC5ncnVuZGZvcy5jb206NDQzL3VtYy1zc28iLCJ1cm46cmVuZXdfYWRkcmVzcyI6Imh0dHBzOi8vYXplb3BleGRzczA0LmVtZWEuZ3JvdXAuZ3J1bmRmb3MuY29tOjQ0My91bWMtc3NvIiwidXJuOmxvZ291dF9hZGRyZXNzIjoiaHR0cHM6Ly9hemVvcGV4ZHNzMDQuZW1lYS5ncm91cC5ncnVuZGZvcy5jb206NDQzL3VtYy1zc28iLCJ1cm46dW5pZmllZHNlc3Npb24iOiJiNmUyNTdmZi02M2NjLTQyNzAtOWI0OC00OWJiODNjN2JmZTQiLCJ1cm46c2NvcGUiOiJyZWFkIiwidXJuOnRydXN0X2xldmVsIjoidW5yZWdpc3RlcmVkIiwibmJmIjoxNzI4NDc2NjQxLCJleHAiOjE3Mjg0NzcyNDEsImlhdCI6MTcyODQ3NjYzMSwiYXVkIjoidXJuOnVuaWZpZWQifQ.ePKvKKGg1RpXB-Z-BGH95hqfzDWfkZLLE83e-2rQnvA",
};

const tokenString = `${token.header}.${token.body}`;

export function AuthorizePageMock() {
  const [language, setLanguage] = useState<string>("en-US");

  const login = useCallback(() => {
    const queryString =
      routerMode === "hash"
        ? window.location.hash.slice(1)
        : window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const decodedBody = jwtDecode(tokenString) as { [key: string]: string };
    decodedBody["urn:language"] = language;

    const encodedBody = btoa(JSON.stringify(decodedBody));
    const encodedToken = `${token.header}.${encodedBody}`;

    document.location =
      urlParams.get("redirect_uri") +
      `#access_token=${encodedToken}&token_type=bearer&expires_in=600&state=` +
      urlParams.get("state");
  }, [language]);

  return (
    <div className="bg-[url('/simenslogo.png')] bg-cover bg-black flex w-full h-full justify-center items-center">
      <div className="flex flex-col gap-5 bg-[#23233c] w-1/2 h-1/3 p-2">
        <h1 className="text-white text-2xl text-center p-5">
          Mock Opcenter Execution login
        </h1>
        <Select value={language} onValueChange={setLanguage}>
          <SelectTrigger className="w-full [&[data-state=open]>svg]:-rotate-180 bg-[#23233c] text-white border rounded-none">
            <SelectPrimitive.SelectValue placeholder="Change Language">
              {language
                ? languages.find((l) => l.value === language)?.name
                : ""}
            </SelectPrimitive.SelectValue>
            <SelectPrimitive.Icon asChild>
              <ChevronDown
                className={cn("h-5 w-5 transition-transform duration-200")}
              />
            </SelectPrimitive.Icon>
          </SelectTrigger>
          <SelectContent className="bg-[#23233c] text-white border">
            {languages.map(({ name, value }) => (
              <SelectItem className="cursor-pointer" key={value} value={value}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          onClick={login}
          className="bg-[#da6914] text-white self-end mt-auto p-2 m-2 text-sm w-1/3 "
        >
          Sign In
        </button>
      </div>
    </div>
  );
}
